import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { Disposable } from '@patsnap/tahiti-core';
import { CurrentTaskService } from '../current-task-service';
import { SharePageService } from '@/services/share-page/share-page-service';

@injectable()
export class CurrentTaskServiceImpl implements CurrentTaskService {
    private _manualTaskId?: string;

    constructor(
        @inject(SharePageService)
        private readonly _sharePageService: SharePageService,
    ) {}

    get taskId(): string {
        // 优先使用手动设置的 taskId
        if (this._manualTaskId) {
            return this._manualTaskId;
        }

        const urlTaskId = this._parseTaskIdFromUrl();
        return urlTaskId || this._sharePageService.taskId;
    }

    get taskType(): string {
        return this._parseTaskTypeFromUrl();
    }

    isCurrentTask(taskId: string): boolean {
        return this.taskId === taskId;
    }

    setManualTaskId(taskId: string): Disposable {
        this._manualTaskId = taskId;

        // 返回 Disposable，调用时清除手动设置的 taskId
        return {
            dispose: () => {
                this._manualTaskId = undefined;
            },
        };
    }

    /**
     * 从 URL hash 中解析 taskId
     * 支持 taskId 和 task_id 两种参数名
     * @returns taskId 或空字符串
     */
    private _parseTaskIdFromUrl(): string {
        const hash = window.location.hash;
        const queryStart = hash.indexOf('?');

        if (queryStart === -1) {
            return '';
        }

        const queryString = hash.substring(queryStart + 1);
        const params = new URLSearchParams(queryString);

        // 优先使用 taskId，回退到 task_id
        return params.get('taskId') || params.get('task_id') || '';
    }

    /**
     * 从 URL hash 中解析路由名称作为 taskType
     * URL 格式: #/AI_FTO_SEARCH?taskId=xxx
     * @returns 路由名称（taskType）
     */
    private _parseTaskTypeFromUrl(): string {
        const hash = window.location.hash;

        if (!hash || hash.length <= 1) {
            return '';
        }

        // 移除开头的 '#'
        const path = hash.substring(1);

        // 提取路径部分（去除 query string）
        const queryStart = path.indexOf('?');
        const routePath = queryStart === -1 ? path : path.substring(0, queryStart);

        // 移除开头的 '/' 得到路由名称
        return routePath.startsWith('/') ? routePath.substring(1) : routePath;
    }
}
